import useLocalization from "@/hooks/useLocalization";
import useReservation from "@/hooks/useReservation";
import useVenue from "@/hooks/useVenue";
import icons from "@/tools/icons";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { enUS, ja, th } from "date-fns/locale";
import { useMemo, useState } from "react";
import SearchMenu from "../../../components/SearchMenu/SearchMenu";

export default function SearchBar() {
  const [expanded, setExpanded] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { from, venueId, productId, resetSearch, hasSearchParams } =
    useReservation();
  const { t, localization } = useLocalization();
  const adapterLocale = useMemo(() => {
    switch (localization) {
      case "ja":
        return ja;
      case "th":
        return th;
      default:
        return enUS;
    }
  }, [localization]);
  const dateAdapter = useMemo(
    () => new AdapterDateFns({ locale: adapterLocale }),
    [adapterLocale],
  );
  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev); // line 24
  };

  const { venueMap, productMap } = useVenue();

  const venue = venueMap.get(venueId || "");
  const product = productMap.get(productId || "");

  return (
    <Accordion
      disableGutters
      expanded={expanded}
      defaultExpanded={false}
      onChange={(_, x) => setExpanded(x)}
      sx={{
        "& .MuiAccordionSummary-content": {
          marginTop: "8px",
          marginBottom: "8px",
          minWidth: 0,
        },
      }}
    >
      <AccordionSummary data-testid="search-accordian">
        <Stack
          direction="row"
          sx={{ width: "100%" }}
          alignItems="center"
          gap={2}
        >
          <Stack
            direction="column"
            justifyContent="center"
            sx={{ flexGrow: 1, minWidth: 0 }}
            gap={1}
          >
            {venue && <RowWithIcon body={venue.name} Icon={icons.Venue} />}
            {product && <RowWithIcon body={product.name} Icon={icons.Tour} />}
            {from && (
              <RowWithIcon
                body={dateAdapter.formatByString(from, "LLLL yyyy")}
                Icon={icons.Calendar}
              />
            )}
          </Stack>
          <Stack direction="row" alignItems="flex-start" gap={1}>
            {hasSearchParams && (
              <IconButton
                onClick={(event) => {
                  event.stopPropagation(); // line 66
                  resetSearch();
                }}
              >
                <icons.Close />
              </IconButton>
            )}
            <IconButton
              data-testid="search-button"
              onClick={toggleMenu}
              aria-label={
                isMenuOpen
                  ? t("command:closeWhat", {
                      interpolation: { what: t("nouns:searchMenu") },
                    })
                  : t("command:openWhat", {
                      interpolation: { what: t("nouns:searchMenu") },
                    })
              }
            >
              <icons.Search />
            </IconButton>
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <SearchMenu
          onSearch={async () => {
            setExpanded(false); // line 94
          }}
        />
      </AccordionDetails>
    </Accordion>
  );
}

type RowWithIconProps = {
  body: string;
  Icon: (typeof icons)[keyof typeof icons];
};
function RowWithIcon({ body, Icon }: RowWithIconProps) {
  return (
    <Stack direction="row" gap={1} alignItems="center">
      <Icon />
      <Typography
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {body}
      </Typography>
    </Stack>
  );
}
